import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Col, Empty, Form, Input, InputNumber, Modal, Row, Select, Space, Switch, Tag, Tooltip, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined, StopOutlined } from '@ant-design/icons';
import { get, post, put, patch, del } from '../../../api/client';
import { LEVEL_COLOR, type PlanRow, type LevelRow } from '../types';

const TEAL = '#0d9488';
const CYCLE_SHORT: Record<string, string> = { month: '月', quarter: '季', year: '年' };
const CYCLE_DEFAULT_MONTHS: Record<string, number> = { month: 1, quarter: 3, year: 12 };

/**
 * 会员套餐管理（SaaS 定价卡片式 CRUD）。
 * - GET/POST/PUT/DELETE /admin/membership/plans，PATCH /admin/membership/plans/:id/active 快速启停。
 * - 金额后端已 Number() 归一，请求与响应同为 number，表单值可直接回填。
 * - 支持按季度 / 按年 / 自动续费（对应需求 #5）。
 */
export function PlansTab() {
  const { message, modal } = App.useApp();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<PlanRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();

  const { data, isLoading } = useQuery({
    queryKey: ['membership-plans'],
    queryFn: () => get<PlanRow[]>('/admin/membership/plans'),
  });
  const plans = data ?? [];

  const { data: levelData } = useQuery({
    queryKey: ['membership-levels'],
    queryFn: () => get<LevelRow[]>('/admin/membership/levels'),
  });
  const levels = levelData ?? [];
  const levelName = (code: string) => levels.find((l) => l.code === code)?.name ?? code;

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['membership-plans'] });

  const save = useMutation({
    mutationFn: ({ id, values }: { id?: string; values: Record<string, unknown> }) =>
      id ? put(`/admin/membership/plans/${id}`, values) : post('/admin/membership/plans', values),
    onSuccess: () => {
      message.success('套餐已保存');
      setEditing(null);
      setCreating(false);
      invalidate();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '保存失败'),
  });

  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      patch(`/admin/membership/plans/${id}/active`, { isActive: active }),
    onSuccess: () => {
      message.success('套餐状态已更新');
      invalidate();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '操作失败'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => del(`/admin/membership/plans/${id}`),
    onSuccess: () => {
      message.success('套餐已删除');
      invalidate();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '删除失败'),
  });

  const openCreate = () => {
    setCreating(true);
    form.resetFields();
    form.setFieldsValue({
      billingCycle: 'month',
      durationMonths: 1,
      price: 0,
      autoRenewSupported: true,
      isActive: true,
      sortOrder: plans.length,
      levelCode: levels.find((l) => l.code !== 'free')?.code ?? levels[0]?.code,
    });
  };

  const openEdit = (p: PlanRow) => {
    setEditing(p);
    form.setFieldsValue({
      planCode: p.planCode,
      levelCode: p.levelCode,
      name: p.name,
      billingCycle: p.billingCycle,
      durationMonths: p.durationMonths,
      price: p.price,
      renewPrice: p.renewPrice ?? undefined,
      autoRenewSupported: p.autoRenewSupported,
      isActive: p.isActive,
      sortOrder: p.sortOrder,
    });
  };

  const submit = async () => {
    try {
      const v = await form.validateFields();
      save.mutate({
        id: editing?.id,
        values: {
          ...(editing ? {} : { planCode: v.planCode }),
          levelCode: v.levelCode,
          name: v.name,
          billingCycle: v.billingCycle,
          durationMonths: v.durationMonths,
          price: v.price,
          renewPrice: v.renewPrice ?? undefined,
          autoRenewSupported: v.autoRenewSupported ?? false,
          isActive: v.isActive ?? true,
          sortOrder: v.sortOrder ?? 0,
        },
      });
    } catch {
      /* 校验失败 */
    }
  };

  const askDelete = (p: PlanRow) => {
    modal.confirm({
      title: '删除套餐',
      content: `确认删除套餐「${p.name}」？该操作不可撤销。`,
      okButtonProps: { danger: true },
      okText: '删除',
      cancelText: '取消',
      onOk: () => remove.mutateAsync(p.id),
    });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Typography.Text type="secondary">套餐决定用户可购买的时长与价格，支持月度 / 季度 / 年度及自动续费。</Typography.Text>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          新建套餐
        </Button>
      </div>

      {isLoading ? (
        <Card loading />
      ) : plans.length === 0 ? (
        <Card>
          <Empty description="暂无会员套餐" />
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {plans.map((p) => {
            const inactive = !p.isActive;
            return (
              <Col key={p.id} xs={24} sm={12} lg={8} xxl={6}>
                <Card
                  style={{
                    height: '100%',
                    opacity: inactive ? 0.62 : 1,
                    border: p.isActive ? `1px solid ${TEAL}55` : undefined,
                    boxShadow: p.isActive ? `0 6px 18px ${TEAL}14` : undefined,
                  }}
                  styles={{ body: { padding: 20 } }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Space size={6} wrap>
                      <Tag color={LEVEL_COLOR[p.levelCode] ?? 'blue'}>{levelName(p.levelCode)}</Tag>
                      <Tag>{CYCLE_SHORT[p.billingCycle] ?? p.billingCycle}</Tag>
                      {p.autoRenewSupported && <Tag color="teal">支持自动续费</Tag>}
                    </Space>
                    {p.isActive ? (
                      <Tag icon={<CheckCircleOutlined />} color="success">
                        启用
                      </Tag>
                    ) : (
                      <Tag icon={<StopOutlined />}>停用</Tag>
                    )}
                  </div>

                  <Typography.Title level={5} style={{ margin: '14px 0 4px' }}>
                    {p.name}
                  </Typography.Title>
                  <Typography.Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>
                    {p.planCode}
                  </Typography.Text>

                  <div style={{ margin: '14px 0', display: 'flex', alignItems: 'baseline', gap: 4 }}>
                    <span style={{ fontSize: 16, color: TEAL }}>¥</span>
                    <span style={{ fontSize: 34, fontWeight: 700, lineHeight: 1, color: '#1f2329' }}>
                      {p.price.toFixed(2).replace(/\.00$/, '')}
                    </span>
                    <span style={{ color: '#8a9099' }}>/ {p.durationMonths} 个月</span>
                  </div>

                  <Space size={4} direction="vertical" style={{ fontSize: 12, color: '#8a9099', minHeight: 40 }}>
                    <span>
                      续费价：
                      {p.renewPrice != null ? `¥${p.renewPrice.toFixed(2).replace(/\.00$/, '')}` : '同首次价'}
                    </span>
                    <span>排序：{p.sortOrder}</span>
                  </Space>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: 12,
                      paddingTop: 12,
                      borderTop: '1px dashed #eef0f1',
                    }}
                  >
                    <Space size={4}>
                      <Tooltip title="编辑">
                        <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(p)} />
                      </Tooltip>
                      <Tooltip title="删除">
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => askDelete(p)} />
                      </Tooltip>
                    </Space>
                    <Tooltip title={p.isActive ? '点击停用' : '点击启用'}>
                      <Switch
                        size="small"
                        checked={p.isActive}
                        loading={toggle.isPending}
                        onChange={(active) => toggle.mutate({ id: p.id, active })}
                      />
                    </Tooltip>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      <Modal
        title={editing ? '编辑套餐' : '新建套餐'}
        open={creating || editing !== null}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        onOk={submit}
        okText="保存"
        cancelText="取消"
        confirmLoading={save.isPending}
        width={560}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="planCode"
                label="套餐标识（code）"
                tooltip="唯一英文标识，创建后不可修改"
                rules={[
                  { required: true, message: '请输入套餐标识' },
                  { pattern: /^[a-z0-9_]+$/, message: '仅允许小写字母、数字、下划线' },
                ]}
              >
                <Input placeholder="如 premium_year" disabled={!!editing} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="name" label="套餐名称" rules={[{ required: true, message: '请输入套餐名称' }]}>
                <Input placeholder="如 高级会员·年付" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="levelCode" label="对应等级" rules={[{ required: true, message: '请选择等级' }]}>
                <Select
                  placeholder="选择等级"
                  options={levels
                    .filter((l) => l.code !== 'free')
                    .map((l) => ({ value: l.code, label: `${l.name}（${l.code}）` }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="billingCycle" label="计费周期" rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: 'month', label: '按月' },
                    { value: 'quarter', label: '按季度' },
                    { value: 'year', label: '按年' },
                  ]}
                  onChange={(c: string) => form.setFieldValue('durationMonths', CYCLE_DEFAULT_MONTHS[c])}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="durationMonths" label="时长（月）" rules={[{ required: true, message: '请输入时长' }]}>
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="price" label="价格（元）" rules={[{ required: true, message: '请输入价格' }]}>
                <InputNumber style={{ width: '100%' }} min={0} precision={2} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="renewPrice" label="续费价（元）" tooltip="留空表示续费与首次同价">
                <InputNumber style={{ width: '100%' }} min={0} precision={2} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="sortOrder" label="排序">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="autoRenewSupported" label="支持自动续费" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="isActive" label="启用" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
}
